import { lazy, Suspense, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Rocket, Wand2 } from "lucide-react";

import { deploy, listServers, type DeployResult, type Server } from "@/lib/api";
import { humanizeError } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/ui/field";
import { useZodForm, z, nonEmpty } from "@/lib/validation";
import { useToast } from "@/components/ui/toast";
import { CopyButton, Spinner } from "@/components/Page";
import { ServerToolPicker, type PickerResult } from "@/components/ServerToolPicker";
import { CreatePage, SingleFooter, Section } from "@/components/wizard";
import { AgentAssembly } from "@/components/AgentAssembly";

const PlaybookAuthor = lazy(() => import("@/components/PlaybookAuthor").then((m) => ({ default: m.PlaybookAuthor })));

const schema = z.object({ name: nonEmpty("Name").max(64, "Keep it under 64 characters") });
type Values = z.infer<typeof schema>;

type Phase = "choose" | "scratch" | "ai" | "deploying" | "done";

export function CreateAgent() {
  const navigate = useNavigate();
  const toast = useToast();
  const [phase, setPhase] = useState<Phase>("choose");
  const [result, setResult] = useState<DeployResult | null>(null);
  const [deployName, setDeployName] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useZodForm(schema, { defaultValues: { name: "" } });

  const [servers, setServers] = useState<Server[]>([]);
  const [loadingServers, setLoadingServers] = useState(true);
  const [serversErr, setServersErr] = useState<string | null>(null);
  const [picker, setPicker] = useState<PickerResult>({ selection: undefined, isEmpty: false });

  useEffect(() => {
    let cancelled = false;
    listServers()
      .then((all) => !cancelled && setServers(all.filter((s) => s.connected)))
      .catch((e) => !cancelled && setServersErr(humanizeError(e, "Couldn't load integrations")))
      .finally(() => !cancelled && setLoadingServers(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const noServers = !loadingServers && !serversErr && servers.length === 0;

  async function onDeploy(values: Values) {
    const selection = noServers ? { servers: [] } : picker.selection;
    setDeployName(values.name);
    setPhase("deploying"); // full assembly view while the microVM provisions
    try {
      const r = await deploy(values.name, "", undefined, selection);
      setResult(r);
      setPhase("done");
    } catch (e) {
      toast.error(humanizeError(e, "Couldn't deploy the agent"));
      setPhase("scratch");
    }
  }

  // "Build with AI" → the existing fullscreen author. Deploy lands on the same
  // success state as the scratch flow.
  if (phase === "ai") {
    return (
      <Suspense fallback={null}>
        <PlaybookAuthor
          projectId={null}
          agentName="New agent"
          subdomain=""
          onClose={() => setPhase("choose")}
          onDeployed={(r) => {
            setResult(r);
            setPhase("done");
          }}
        />
      </Suspense>
    );
  }

  if (phase === "done" && result) {
    return (
      <CreatePage
        title="Agent deployed"
        description="Your isolated /mcp endpoint is live."
        backTo="/agents"
        footer={
          <Button className="ml-auto" onClick={() => navigate(result.project_id ? `/agents/${result.project_id}` : "/agents")}>
            Open agent
          </Button>
        }
      >
        <Section title="Connection" description="Agents connect with an Authorization: Bearer <token> header.">
          <Row label="MCP URL" value={`${result.url}/mcp`} />
          {result.default_token && (
            <div>
              <div className="mb-1 text-xs font-medium text-muted-foreground">
                Access token — copy it now, you won't see it again.
              </div>
              <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 font-mono text-xs">
                <span className="min-w-0 flex-1 break-all">{result.default_token}</span>
                <CopyButton value={result.default_token} />
              </div>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Manage, rotate, or add tokens in the agent's <span className="font-medium text-foreground">Access</span> tab.
          </p>
        </Section>
      </CreatePage>
    );
  }

  if (phase === "choose") {
    return (
      <CreatePage
        title="New agent"
        description="An isolated sandbox with its own /mcp endpoint, playbook, and access tokens."
        backTo="/agents"
        footer={<Button variant="ghost" className="mr-auto" onClick={() => navigate("/agents")}>Cancel</Button>}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <button
            onClick={() => setPhase("ai")}
            className="flex flex-col gap-1.5 rounded-xl border bg-card p-5 text-left transition-colors hover:border-primary/40 hover:bg-accent"
          >
            <span className="flex items-center gap-2 font-medium">
              <Wand2 className="h-4 w-4 text-primary" /> Build with AI
            </span>
            <span className="text-sm text-muted-foreground">
              Describe the role you're hiring for — the assistant writes the playbook and you deploy in one click.
            </span>
          </button>
          <button
            onClick={() => setPhase("scratch")}
            className="flex flex-col gap-1.5 rounded-xl border bg-card p-5 text-left transition-colors hover:border-primary/40 hover:bg-accent"
          >
            <span className="flex items-center gap-2 font-medium">
              <Rocket className="h-4 w-4" /> From scratch
            </span>
            <span className="text-sm text-muted-foreground">Set it up yourself: name the agent and pick its tools.</span>
          </button>
        </div>
      </CreatePage>
    );
  }

  // Provisioning the microVM — a full assembly view at the SAME width as the form.
  if (phase === "deploying") {
    return (
      <div className="mx-auto w-full max-w-4xl">
        <AgentAssembly name={deployName} />
      </div>
    );
  }

  // scratch
  return (
    <CreatePage
      title="New agent"
      description="Bundle integrations into an isolated sandbox — or create one with just code execution + storage."
      backTo="/agents"
      footer={
        <SingleFooter
          onCancel={() => setPhase("choose")}
          onPrimary={() => void handleSubmit(onDeploy)()}
          submitting={isSubmitting}
          submitLabel={isSubmitting ? "Deploying…" : "Deploy agent"}
          primaryDisabled={loadingServers || !!serversErr}
        />
      }
    >
      <Section title="Name" description="A URL is assigned automatically — add a custom domain later.">
        <Field label="Name" htmlFor="ag-name" error={errors.name?.message}>
          <Input id="ag-name" autoFocus placeholder="my-agent" aria-invalid={!!errors.name} {...register("name")} />
        </Field>
      </Section>

      <Section title="Integrations & tools" description="Pick which connected integrations (and tools) this agent can use.">
        {loadingServers ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner /> Loading integrations…
          </div>
        ) : serversErr ? (
          <p className="text-sm text-destructive">{serversErr}</p>
        ) : noServers ? (
          <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
            No integrations connected yet. This agent will have{" "}
            <span className="font-medium text-foreground">code execution and persistent storage</span> only — connect
            one anytime to add its tools.
          </div>
        ) : (
          <>
            <ServerToolPicker servers={servers} defaultEmpty onChange={setPicker} />
            {picker.isEmpty && (
              <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
                No integrations selected — this agent gets code execution + storage only. Pick the ones it should use
                above (or “Select all”); you can change this anytime.
              </div>
            )}
          </>
        )}
      </Section>

    </CreatePage>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-24 shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 flex-1 truncate font-mono text-xs">{value}</span>
      <CopyButton value={value} />
    </div>
  );
}
